import { useState, useEffect, useRef, useCallback } from 'react'
import logo from '../assets/logo.png'
import Editor from '@monaco-editor/react'
import { useAuth } from '../contexts/AuthContext'
import { listConnectors, getConnector, saveConnector, uploadConnector, approveConnector, deleteConnector, getConnectorUsages } from '../utils/api'
import { canApprove, canDelete } from '../utils/permissions'
import RunModal from '../components/RunModal'
import DocsModal from '../components/DocsModal'
import SnippetsModal from '../components/SnippetsModal'

// ---------------------------------------------------------------------------
// Icons (inline SVG helpers)
// ---------------------------------------------------------------------------
const Icon = ({ d, size = 16, stroke = 'currentColor', sw = 1.5, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d={d} />
  </svg>
)

const PythonIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M11.5 2C9.6 2 8 3.2 8 5v1h7v1H6C4 7 2 8.6 2 11.5S4 16 6 16h2v-2c0-2 1.5-3 3.5-3h5c1.7 0 3-1.3 3-3V5c0-1.8-1.6-3-3.5-3h-4.5z" fill="#3572A5" />
    <path d="M12.5 22c1.9 0 3.5-1.2 3.5-3v-1H9v-1h9c2 0 4-1.6 4-4.5S20 8 18 8h-2v2c0 2-1.5 3-3.5 3h-5c-1.7 0-3 1.3-3 3v3c0 1.8 1.6 3 3.5 3h4.5z" fill="#FFD43B" />
    <circle cx="10" cy="5.5" r="1" fill="white" />
    <circle cx="14" cy="18.5" r="1" fill="white" />
  </svg>
)

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
function Toast({ toast }) {
  if (!toast) return null
  const colors = {
    success: 'bg-[#1e3a1e] border-[#4ec994] text-[#4ec994]',
    error:   'bg-[#3a1e1e] border-[#f44747] text-[#f44747]',
    info:    'bg-[#1e2a3a] border-[#0078d4] text-[#6cb6f5]',
  }
  return (
    <div className={`toast fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm font-medium shadow-xl ${colors[toast.type] || colors.info}`}>
      {toast.type === 'success' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
      {toast.type === 'error'   && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
      {toast.type === 'info'    && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>}
      {toast.message}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const formatBytes = (n) => n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------
const RECENT_KEY = 'dp_recent_files'
const RECENT_MAX = 5
const SAVE_STATUS_KEY = 'dp_save_default_status'

const loadRecentFiles = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]') } catch { return [] }
}

const saveRecentFile = (filename, displayName) => {
  const prev = loadRecentFiles().filter((r) => r.filename !== filename)
  const next = [{ filename, displayName, accessedAt: new Date().toISOString() }, ...prev].slice(0, RECENT_MAX)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  return next
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function EditorPage() {
  const { logout, username } = useAuth()

  // Sidebar
  const [connectors, setConnectors]   = useState([])
  const [loadingFiles, setLoadingFiles] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [recentFiles, setRecentFiles] = useState(loadRecentFiles)

  // Tabs: { filename, content, originalContent, loading }
  const [tabs, setTabs]       = useState([])
  const [activeTab, setActiveTab] = useState(null)

  // UI state
  const [isSaving, setIsSaving]       = useState(false)
  const [showRunModal, setShowRunModal] = useState(false)
  const [showDocs, setShowDocs] = useState(false)
  const [showSnippets, setShowSnippets] = useState(false)
  const [isEditorFocused, setIsEditorFocused] = useState(false)
  const [toast, setToast]             = useState(null)
  const [cursor, setCursor]           = useState({ line: 1, col: 1 })
  const [uploadError, setUploadError] = useState('')
  const [fileFilter, setFileFilter]   = useState('')

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [pendingFile, setPendingFile]         = useState(null)
  const [uploadForm, setUploadForm]           = useState({ displayName: '', description: '', approved: false })

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, connector: null, usages: [], loadingUsages: false, confirm: '', deleting: false })

  // Save status choice modal
  const [saveStatusModal, setSaveStatusModal] = useState({ open: false, filename: null, selectedStatus: localStorage.getItem(SAVE_STATUS_KEY) || 'pending' })

  const editorRef    = useRef(null)
  const monacoRef    = useRef(null)
  const uploadRef    = useRef(null)
  const toastTimeout = useRef(null)

  // ---------------------------------------------------------------------------
  // Toast helper
  // ---------------------------------------------------------------------------
  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimeout.current)
    const msg = message instanceof Error
      ? (message.response?.data?.detail || message.message)
      : String(message ?? '')
    setToast({ type, message: msg })
    toastTimeout.current = setTimeout(() => setToast(null), 3500)
  }, [])

  // ---------------------------------------------------------------------------
  // Load file list
  // ---------------------------------------------------------------------------
  const loadConnectors = useCallback(async () => {
    setLoadingFiles(true)
    try {
      const { data } = await listConnectors()
      setConnectors(data)
    } catch(e) {
      showToast('error', `Failed to load connector list ${e}`)
    } finally {
      setLoadingFiles(false)
    }
  }, [showToast])

  useEffect(() => { loadConnectors() }, [loadConnectors])

  // ---------------------------------------------------------------------------
  // Open / switch file
  // ---------------------------------------------------------------------------
  const openFile = useCallback(async (filename) => {
    // Already open → just activate
    const existing = tabs.find((t) => t.filename === filename)
    if (existing) { setActiveTab(filename); return }

    // Add loading placeholder
    setTabs((prev) => [...prev, { filename, content: '', originalContent: '', loading: true }])
    setActiveTab(filename)

    try {
      const { data } = await getConnector(filename)
      setTabs((prev) =>
        prev.map((t) =>
          t.filename === filename
            ? { ...t, content: data.content, originalContent: data.content, loading: false }
            : t
        )
      )
      const connector = connectors.find((c) => c.filename === filename)
      const displayName = connector?.display_name || filename
      setRecentFiles(saveRecentFile(filename, displayName))
    } catch {
      showToast('error', `Failed to open ${filename}`)
      setTabs((prev) => prev.filter((t) => t.filename !== filename))
      setActiveTab((prev) => (prev === filename ? null : prev))
    }
  }, [tabs, connectors, showToast])

  // ---------------------------------------------------------------------------
  // Close tab
  // ---------------------------------------------------------------------------
  const closeTab = useCallback((filename, e) => {
    e?.stopPropagation()
    const isDirty = tabs.find((t) => t.filename === filename)?.content !==
                    tabs.find((t) => t.filename === filename)?.originalContent
    if (isDirty && !window.confirm(`${filename} has unsaved changes. Close anyway?`)) return

    const remaining = tabs.filter((t) => t.filename !== filename)
    setTabs(remaining)
    if (activeTab === filename) {
      setActiveTab(remaining[remaining.length - 1]?.filename ?? null)
    }
  }, [tabs, activeTab])

  // ---------------------------------------------------------------------------
  // Editor change
  // ---------------------------------------------------------------------------
  const handleEditorChange = useCallback((value) => {
    setTabs((prev) =>
      prev.map((t) => t.filename === activeTab ? { ...t, content: value ?? '' } : t)
    )
  }, [activeTab])

  // ---------------------------------------------------------------------------
  // Insert snippet at cursor position with auto-indentation
  // ---------------------------------------------------------------------------
  const insertSnippet = useCallback((snippet) => {
    if (!editorRef.current || !monacoRef.current) return

    const editor = editorRef.current
    const monaco = monacoRef.current
    const position = editor.getPosition()

    // Get current line indentation
    const model = editor.getModel()
    const currentLineContent = model.getLineContent(position.lineNumber)
    const currentIndentation = currentLineContent.match(/^\s*/)[0]

    // Indent each line of the snippet to match current indentation
    const snippetLines = snippet.code.split('\n')
    const indentedCode = snippetLines
      .map((line, idx) => {
        // Keep first line as-is (it will be on the current line)
        if (idx === 0) return line
        // Indent subsequent lines
        return currentIndentation + line
      })
      .join('\n')

    // Insert snippet at current cursor position
    const range = new monaco.Range(
      position.lineNumber,
      position.column,
      position.lineNumber,
      position.column
    )

    editor.executeEdits('insert-snippet', [
      {
        range,
        text: indentedCode,
      },
    ])

    // Move cursor to end of inserted code
    const lines = indentedCode.split('\n')
    const newLine = position.lineNumber + lines.length - 1
    const newColumn = lines.length > 1 ? lines[lines.length - 1].length + 1 : position.column + indentedCode.length
    editor.setPosition({ lineNumber: newLine, column: newColumn })

    // Update the tab content
    setTabs((prev) =>
      prev.map((t) =>
        t.filename === activeTab ? { ...t, content: editor.getValue() } : t
      )
    )

    showToast('success', `Inserted snippet: ${snippet.name}`)
  }, [activeTab, showToast])

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = useCallback(() => {
    if (!activeTab || isSaving) return
    const tab = tabs.find((t) => t.filename === activeTab)
    if (!tab || tab.loading) return

    // Show modal to choose status
    setSaveStatusModal({
      open: true,
      filename: activeTab,
      selectedStatus: localStorage.getItem(SAVE_STATUS_KEY) || 'pending'
    })
  }, [activeTab, isSaving, tabs])

  const handleConfirmSave = useCallback(async (status) => {
    const filename = saveStatusModal.filename
    if (!filename) return

    // Save preference to localStorage
    localStorage.setItem(SAVE_STATUS_KEY, status)

    setIsSaving(true)
    try {
      const tab = tabs.find((t) => t.filename === filename)
      if (!tab) return

      // Save with the chosen status
      await saveConnector(filename, tab.content, status === 'approved')
      setTabs((prev) =>
        prev.map((t) =>
          t.filename === filename ? { ...t, originalContent: t.content } : t
        )
      )
      await loadConnectors() // refresh size/mtime in sidebar
      showToast('success', `${filename} saved${status === 'approved' ? ' (aprovado)' : ' (revisão)'}`)
    } catch (err) {
      const msg = err.response?.data?.detail || err.message
      showToast('error', `Save failed: ${msg}`)
    } finally {
      setIsSaving(false)
      setSaveStatusModal({ open: false, filename: null, selectedStatus: 'pending' })
    }
  }, [saveStatusModal, tabs, loadConnectors, showToast])

  // ---------------------------------------------------------------------------
  // Run — auto-save if dirty, then open modal
  // ---------------------------------------------------------------------------
  const handleRun = useCallback(async () => {
    if (!activeTab) return
    const tab = tabs.find((t) => t.filename === activeTab)
    if (!tab || tab.loading) return
    if (tab.content !== tab.originalContent) {
      await handleSave()
    }
    setShowRunModal(true)
  }, [activeTab, tabs, handleSave])

  // ---------------------------------------------------------------------------
  // Monaco mount — attach Ctrl+S and Ctrl+K for snippets
  // ---------------------------------------------------------------------------
  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor
    monacoRef.current = monaco
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSave()
    })
    // Ctrl+K to open snippets (only when editor is focused)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK, () => {
      setShowSnippets(true)
    })
    editor.onDidChangeCursorPosition((e) => {
      setCursor({ line: e.position.lineNumber, col: e.position.column })
    })
    editor.onDidFocusEditorText(() => {
      setIsEditorFocused(true)
    })
    editor.onDidBlurEditorText(() => {
      setIsEditorFocused(false)
    })
  }, [handleSave])

  // Re-bind Ctrl+S when handleSave changes (deps change on each render)
  useEffect(() => {
    if (!editorRef.current || !monacoRef.current) return
    const disposable = editorRef.current.addCommand(
      monacoRef.current.KeyMod.CtrlCmd | monacoRef.current.KeyCode.KeyS,
      handleSave
    )
    return () => disposable?.dispose?.()
  }, [handleSave])

  // ---------------------------------------------------------------------------
  // New — opens the New Connector modal
  // ---------------------------------------------------------------------------
  const [showNewModal, setShowNewModal] = useState(false)
  const [newFilename, setNewFilename] = useState('new_connector.py')
  const [newType, setNewType] = useState(null)       // 'blank' | 'template' | 'from'
  const [newFromFile, setNewFromFile] = useState('')
  const [newFromFilter, setNewFromFilter] = useState('')
  const [newFileError, setNewFileError] = useState('')
  const [newCreating, setNewCreating] = useState(false)

  const validateNewFilename = (name) => {
    if (!name.trim()) return 'Filename is required'
    if (!/^[a-z0-9_]+_connector\.py$/.test(name.trim())) return 'Use only lowercase letters, numbers and _ — must end with _connector.py'
    return ''
  }

  const toClassName = (filename) =>
    filename.replace(/_connector\.py$/, '')
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join('') + 'Connector'

  const buildTemplate = (className) => `from typing import Any, Dict, Optional


class ${className}:
    """
    ${className} — N4H Integration Connector

    Describe the purpose of this connector here.
    """

    def __init__(self, config: Dict[str, Any]) -> None:
        """
        Initialize the connector with configuration from the processor.

        Args:
            config: Dict containing:
                - integration_id: UUID string of the integration
                - company_id: UUID string of the company
                - tracer: ConnectorTracer instance (simplified interface)
        """
        self.integration_id = config.get("integration_id")
        self.company_id = config.get("company_id")
        self.tracer = config.get("tracer")  # Use self.tracer.success() and self.tracer.error()

    # Note: before_process() is optional. Only implement if you need preprocessing.
    # The process() method receives the raw CloudEvent data by default.

    async def process(self, event_data: Dict[str, Any]) -> Dict[str, Any]:
        """
        Main connector logic — async function that processes CloudEvents.

        The platform calls this for each incoming CloudEvent.

        SIMPLIFIED TRACER USAGE (DRY principle):
        Instead of verbose try/except around each step, just use:

            self.tracer.success("step_name", {"metadata": "value"})
            self.tracer.error("step_name", error_exception, {"metadata": "value"})
            self.tracer.skip("step_name", "reason why skipped")

        ConnectorTracer handles errors internally - your code stays clean!

        Args:
            event_data: CloudEvent dict with:
                - id: event ID
                - type: event type (e.g., "com.hospital.exam.created")
                - source: event source URL
                - data: custom payload dict

        Returns:
            {"status": "success"|"failed", "message": "...", ...}
            Any fields you return are saved in the trace!
        """
        try:
            # EXAMPLE 1: Record a successful step
            self.tracer.success("validate_input", {
                "event_type": event_data.get("type"),
                "company_id": self.company_id,
            })

            # EXAMPLE 2: Conditional processing
            data = event_data.get("data", {})
            if not data:
                self.tracer.skip("process_data", "Empty payload")
                return {
                    "status": "success",
                    "message": "Event empty, skipped",
                    "webhook_status_code": 200,
                }

            # TODO: implement your integration logic
            # Example:
            # import httpx
            # async with httpx.AsyncClient() as client:
            #     response = await client.post("https://api.example.com/events", json=data)
            #     self.tracer.success("send_to_api", {"status_code": response.status_code})

            return {
                "status": "success",
                "message": "Event processed successfully",
                "webhook_status_code": 200,
            }

        except Exception as exc:
            # Record the error (no need for if tracer check!)
            self.tracer.error("process", exc, {"event_id": event_data.get("id")})
            return {
                "status": "failed",
                "message": str(exc),
                "webhook_status_code": None,
            }

    def health_check(self) -> Dict[str, Any]:
        """
        Check the availability of the target service.

        Called periodically by the platform to monitor connector health.

        Returns:
            {"healthy": bool, "message": str}
        """
        try:
            # TODO: verify connectivity with the target service
            # Example:
            # response = requests.get(f"{self.base_url}/health", timeout=5)
            # return {"healthy": response.ok, "message": "Service available"}

            return {"healthy": True, "message": "Connector initialized"}

        except Exception as exc:
            return {"healthy": False, "message": str(exc)}
`

  const handleNewConnector = useCallback(() => {
    setNewFilename('new_connector.py')
    setNewType(null)
    setNewFromFile('')
    setNewFromFilter('')
    setNewFileError('')
    setShowNewModal(true)
  }, [])

  const handleCreateConnector = useCallback(async () => {
    const err = validateNewFilename(newFilename)
    if (err) { setNewFileError(err); return }
    if (!newType) { setNewFileError('Select a connector type'); return }
    if (newType === 'from' && !newFromFile) { setNewFileError('Select a source connector'); return }

    setNewCreating(true)
    setNewFileError('')
    try {
      let content = ''
      const className = toClassName(newFilename.trim())

      if (newType === 'template') {
        content = buildTemplate(className)
      } else if (newType === 'from') {
        const { data } = await getConnector(newFromFile)
        const srcClass = toClassName(newFromFile)
        content = data.content.replace(new RegExp(srcClass, 'g'), className)
      }
      // blank → content stays ''

      const file = new File([content], newFilename.trim(), { type: 'text/x-python' })
      const pretty = newFilename.trim()
        .replace(/_connector\.py$/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())

      await uploadConnector(file, pretty, '', false)
      setShowNewModal(false)
      await loadConnectors()
      openFile(newFilename.trim())
      showToast('success', `${newFilename.trim()} created`)
    } catch (e) {
      setNewFileError('Failed to create connector: ' + (e.response?.data?.detail || e.message))
    } finally {
      setNewCreating(false)
    }
  }, [newFilename, newType, newFromFile, loadConnectors, openFile, showToast])

  // ---------------------------------------------------------------------------
  // Upload — step 1: validate file and open modal to collect metadata
  // ---------------------------------------------------------------------------
  const handleFileInput = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setUploadError('')

    if (!/^[a-zA-Z0-9_]+_connector\.py$/.test(file.name)) {
      setUploadError('File must be named *_connector.py')
      return
    }
    // Pre-fill display name from filename (strip _connector.py, prettify)
    const pretty = file.name
      .replace(/_connector\.py$/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
    setPendingFile(file)
    setUploadForm({ displayName: pretty, description: '', approved: false })
    setShowUploadModal(true)
  }, [])

  // ---------------------------------------------------------------------------
  // Upload — step 2: submit modal form
  // ---------------------------------------------------------------------------
  const handleUploadSubmit = useCallback(async () => {
    if (!pendingFile || !uploadForm.displayName.trim()) return
    if (uploadForm.approved && !canApprove(username)) {
      showToast('error', `Usuário ${username} sem permissão para aprovar conectores.`)
      return
    }
    setShowUploadModal(false)
    try {
      await uploadConnector(pendingFile, uploadForm.displayName.trim(), uploadForm.description.trim(), uploadForm.approved)
      showToast('success', `${pendingFile.name} uploaded`)
      await loadConnectors()
      openFile(pendingFile.name)
    } catch (err) {
      const detail = err.response?.data?.detail
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || err.message)
      showToast('error', `Upload failed: ${msg}`)
    } finally {
      setPendingFile(null)
    }
  }, [pendingFile, uploadForm, loadConnectors, openFile, showToast])

  // ---------------------------------------------------------------------------
  // Approve / revoke
  // ---------------------------------------------------------------------------
  const handleToggleApprove = useCallback(async (filename, currentApproved) => {
    if (!canApprove(username)) {
      showToast('error', `Usuário ${username} sem permissão para aprovar/revogar conectores.`)
      return
    }
    try {
      await approveConnector(filename, !currentApproved)
      await loadConnectors()
      showToast('success', !currentApproved ? `${filename} aprovado` : `Aprovação de ${filename} revogada`)
    } catch (err) {
      showToast('error', err.response?.data?.detail || err.message)
    }
  }, [loadConnectors, showToast, username])

  // ---------------------------------------------------------------------------
  // Delete — step 1: open modal, fetch usages
  // ---------------------------------------------------------------------------
  const handleDeleteClick = useCallback(async (connector, e) => {
    e.stopPropagation()
    if (!canDelete(username)) {
      showToast('error', `Usuário ${username} sem permissão para remover conectores.`)
      return
    }
    setDeleteModal({ open: true, connector, usages: [], loadingUsages: true, confirm: '', deleting: false })
    try {
      const { data } = await getConnectorUsages(connector.filename)
      setDeleteModal((m) => ({ ...m, usages: data, loadingUsages: false }))
    } catch {
      setDeleteModal((m) => ({ ...m, usages: [], loadingUsages: false }))
    }
  }, [username, showToast])

  // ---------------------------------------------------------------------------
  // Delete — step 2: confirm and execute
  // ---------------------------------------------------------------------------
  const handleDeleteConfirm = useCallback(async () => {
    const { connector } = deleteModal
    if (!connector) return
    setDeleteModal((m) => ({ ...m, deleting: true }))
    try {
      await deleteConnector(connector.filename)
      setDeleteModal({ open: false, connector: null, usages: [], loadingUsages: false, confirm: '', deleting: false })
      // Close tab if open
      setTabs((prev) => prev.filter((t) => t.filename !== connector.filename))
      setActiveTab((prev) => prev === connector.filename ? null : prev)
      await loadConnectors()
      showToast('success', `${connector.display_name} removido`)
    } catch (err) {
      showToast('error', err.response?.data?.detail || err.message)
      setDeleteModal((m) => ({ ...m, deleting: false }))
    }
  }, [deleteModal, loadConnectors, showToast])

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  const currentTab = tabs.find((t) => t.filename === activeTab)
  const isDirty    = currentTab
    ? currentTab.content !== currentTab.originalContent
    : false

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#1e1e1e] text-[#cccccc]">
      <Toast toast={toast} />

      {/* ── Title Bar ─────────────────────────────────────────────────── */}
      <header className="grid h-14 bg-[#3c3c3c] border-b border-[#252526] flex-shrink-0 select-none px-4" style={{ gridTemplateColumns: '1fr auto 1fr' }}>

        {/* Left: logo + docs */}
        <div className="flex items-center gap-2.5">
          <div className="logo-spin-border inline-flex items-center justify-center shrink-0">
            <img src={logo} alt="SF" className="object-cover rounded-full" style={{ width: '32px', height: '32px' }} />
          </div>
          <span className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>
            Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
          </span>
          <span className="text-[#858585] text-xs font-normal">/ Dev Portal</span>
          <div className="w-px h-4 bg-[#555] mx-1" />
          <button
            onClick={() => setShowDocs(true)}
            title="Guia do Desenvolvedor"
            className="flex items-center gap-1.5 text-xs font-semibold text-[#ddb900] bg-[#ddb90018] hover:bg-[#ddb90028] border border-[#ddb900] px-2.5 py-1 rounded transition"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Guia do Desenvolvedor
          </button>
        </div>

        {/* Center: toolbar */}
        <div className="flex items-center gap-0.5 bg-[#2d2d2d] border border-[#4a4a4a] rounded-md px-1 my-1">
          {/* Upload */}
          <button
            onClick={() => uploadRef.current?.click()}
            title="Upload connector (.py)"
            className="group flex items-center gap-1.5 text-[11px] text-[#aaaaaa] hover:text-white px-3 py-1.5 rounded transition-colors duration-150 hover:bg-[#ffffff10]"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-150 group-hover:scale-125">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Upload
          </button>
          <input ref={uploadRef} type="file" accept=".py" className="hidden" onChange={handleFileInput} />

          <div className="w-px h-4 bg-[#4a4a4a]" />

          {/* Run */}
          <button
            onClick={handleRun}
            disabled={!activeTab || currentTab?.loading || isSaving}
            title={isDirty ? 'Run connector — vai salvar automaticamente antes de executar' : 'Run connector'}
            className="group relative flex items-center gap-1.5 text-[11px] text-[#4ec994] px-3 py-1.5 rounded transition-colors duration-150 hover:bg-[#4ec99415] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="transition-transform duration-150 group-hover:scale-125 group-disabled:scale-100">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            Run
            {isDirty && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#ddb900] text-[#1a1a1a] font-bold leading-none" style={{ fontSize: '9px' }}>!</span>
            )}
          </button>

          <div className="w-px h-4 bg-[#4a4a4a]" />

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={!activeTab || isSaving || !isDirty}
            title="Save (Ctrl+S)"
            className="group flex items-center gap-1.5 text-[11px] text-[#6cb6f5] px-3 py-1.5 rounded transition-colors duration-150 hover:bg-[#0078d420] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-150 group-hover:scale-125">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
              </svg>
            )}
            {isSaving ? 'Saving…' : 'Save'}
          </button>

          <div className="w-px h-4 bg-[#4a4a4a]" />

          {/* Snippets */}
          <button
            onClick={() => setShowSnippets(true)}
            disabled={!activeTab || currentTab?.loading}
            title="Code Snippets (Ctrl+K)"
            className="group flex items-center gap-1.5 text-[11px] text-[#ce9178] px-3 py-1.5 rounded transition-colors duration-150 hover:bg-[#ce917815] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-150 group-hover:scale-125 group-disabled:scale-100">
              <polyline points="16 18 22 12 16 6"/>
              <polyline points="8 6 2 12 8 18"/>
            </svg>
            Snippets
          </button>
        </div>

        {/* Right: user */}
        <div className="flex items-center justify-end gap-2">

          {/* User section */}
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(37,198,164,0.2)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#25C6A4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>{username}</span>
            <button
              onClick={logout}
              title="Sign out"
              className="p-1.5 rounded transition"
              style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#f44747'; e.currentTarget.style.backgroundColor = 'rgba(244,71,71,0.1)' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Activity Bar */}
        <div className="flex flex-col items-center gap-1 w-12 bg-[#333333] border-r border-[#252526] py-2 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            title="Explorer"
            className={`flex items-center justify-center w-9 h-9 rounded transition ${sidebarOpen ? 'text-white bg-[#ffffff15]' : 'text-[#858585] hover:text-white hover:bg-[#ffffff10]'}`}
            style={sidebarOpen ? { borderLeft: '2px solid #0078d4' } : {}}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
          </button>

        </div>

        {/* Sidebar — File Explorer */}
        {sidebarOpen && (
          <div className="flex flex-col w-80 bg-[#252526] border-r border-[#3c3c3c] flex-shrink-0 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#3c3c3c]">
              <span className="text-[10px] font-semibold text-[#858585] uppercase tracking-widest">Connectors</span>
              <span className="text-[10px] text-[#555]">{connectors.length} arquivo{connectors.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Filter input */}
            <div className="px-3 py-2 border-b border-[#3c3c3c]">
              <div className="flex items-center gap-1.5 bg-[#3c3c3c] rounded px-2 py-1">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={fileFilter}
                  onChange={(e) => setFileFilter(e.target.value)}
                  placeholder="Filter files…"
                  className="flex-1 bg-transparent text-xs text-[#cccccc] placeholder-[#555] outline-none"
                />
                {fileFilter && (
                  <button onClick={() => setFileFilter('')} className="text-[#555] hover:text-[#858585]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
            </div>

            {/* File list */}
            <div className="flex-1 overflow-y-auto py-1">
              {loadingFiles ? (
                <div className="flex items-center justify-center py-10 text-[#555] text-xs gap-2">
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Loading…
                </div>
              ) : connectors.length === 0 ? (
                <div className="py-10 px-4 text-center text-xs text-[#555]">
                  No connector files found.<br />
                  <button onClick={() => uploadRef.current?.click()} className="text-[#0078d4] hover:underline mt-1">Upload one</button>
                </div>
              ) : connectors.filter((f) =>
                  f.display_name.toLowerCase().includes(fileFilter.toLowerCase()) ||
                  f.filename.toLowerCase().includes(fileFilter.toLowerCase())
                ).length === 0 ? (
                <div className="py-10 px-4 text-center text-xs text-[#555]">
                  No files match "<span className="text-[#858585]">{fileFilter}</span>"
                </div>
              ) : (
                connectors
                  .filter((f) =>
                    f.display_name.toLowerCase().includes(fileFilter.toLowerCase()) ||
                    f.filename.toLowerCase().includes(fileFilter.toLowerCase())
                  )
                  .map((f) => {
                  const isActive = activeTab === f.filename
                  const isOpen   = tabs.some((t) => t.filename === f.filename)
                  const tabDirty = isOpen && tabs.find((t) => t.filename === f.filename)?.content !==
                                             tabs.find((t) => t.filename === f.filename)?.originalContent
                  return (
                    <div
                      key={f.filename}
                      className={`file-item relative group cursor-pointer border-b border-[#2a2a2a] ${
                        isActive ? 'bg-[#094771]' : 'hover:bg-[#2a2d2e]'
                      }`}
                      onClick={() => openFile(f.filename)}
                    >
                      {/* Trash icon — absolute top-right, visible on hover */}
                      <button
                        title="Remover conector"
                        onClick={(e) => handleDeleteClick(f, e)}
                        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 p-1 rounded text-[#555] hover:text-[#f44747] hover:bg-[#f4474720] transition"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                        </svg>
                      </button>

                      {/* Row 1: icon + name + dirty dot (pr-7 to avoid overlap with trash) */}
                      <div className="flex items-center gap-2 px-3 pt-2 pb-0.5 pr-7">
                        <PythonIcon size={13} />
                        <span className="flex-1 text-xs font-medium truncate text-white">
                          {f.display_name}
                        </span>
                        {tabDirty && (
                          <span className={`text-[10px] flex-shrink-0 ${isActive ? 'text-[#89c9ff]' : 'text-[#0078d4]'}`}>●</span>
                        )}
                      </div>

                      {/* Row 2: filename + status badge */}
                      <div className="flex items-center gap-2 px-3 pb-1.5">
                        <span className={`flex-1 text-[10px] font-mono truncate ${isActive ? 'text-[#7ab8d9]' : 'text-[#555]'}`}>
                          {f.filename}
                        </span>
                        {f.approved
                          ? <span className={`flex-shrink-0 flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 ${
                              isActive
                                ? 'text-[#6effc4] bg-[#4ec99425] border border-[#4ec99460]'
                                : 'text-[#4ec994] bg-[#4ec99415] border border-[#4ec99430]'
                            }`}>
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              Aprovado
                            </span>
                          : <span className={`flex-shrink-0 flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 ${
                              isActive
                                ? 'text-[#c8dde8] bg-[#ffffff15] border border-[#ffffff25]'
                                : 'text-[#858585] bg-[#3c3c3c] border border-[#4a4a4a]'
                            }`}>
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              Pendente
                            </span>
                        }
                      </div>

                      {/* Row 3: approve/revoke — visible on hover */}
                      <div className="flex items-center px-3 pb-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        {f.approved ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleApprove(f.filename, true) }}
                            className={`flex items-center gap-1 text-[10px] rounded px-2 py-0.5 transition border ${
                              isActive
                                ? 'text-[#ffaaaa] border-[#f4474760] hover:bg-[#f4474730] hover:border-[#f44747]'
                                : 'text-[#f44747] border-[#f4474740] hover:bg-[#f4474720] hover:border-[#f44747]'
                            }`}
                          >
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            Revogar
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleApprove(f.filename, false) }}
                            className={`flex items-center gap-1 text-[10px] rounded px-2 py-0.5 transition border ${
                              isActive
                                ? 'text-[#6effc4] border-[#4ec99460] hover:bg-[#4ec99430] hover:border-[#4ec994]'
                                : 'text-[#4ec994] border-[#4ec99440] hover:bg-[#4ec99420] hover:border-[#4ec994]'
                            }`}
                          >
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            Aprovar
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {/* Upload error */}
            {uploadError && (
              <div className="px-3 py-2 text-xs text-[#f44747] bg-[#f4474710] border-t border-[#f4474730]">
                {uploadError}
              </div>
            )}

            {/* Footer actions */}
            <div className="p-3 border-t border-[#3c3c3c] flex items-center gap-1.5">
              {/* Upload */}
              <button
                onClick={() => { setUploadError(''); uploadRef.current?.click() }}
                title="Upload arquivo .py existente"
                className="flex-1 flex items-center justify-center gap-1.5 text-xs text-[#858585] hover:text-white border border-[#3c3c3c] hover:border-[#505050] hover:bg-[#2a2d2e] rounded px-2 py-1.5 transition"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload
              </button>

              {/* New */}
              <button
                onClick={handleNewConnector}
                title="Criar novo conector a partir do template"
                className="flex-1 flex items-center justify-center gap-1.5 text-xs text-[#6cb6f5] bg-[#0078d420] hover:bg-[#0078d435] border border-[#0078d4] rounded px-2 py-1.5 transition"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                New
              </button>

              {/* Refresh */}
              <button
                onClick={loadConnectors}
                title="Atualizar lista"
                className="flex items-center justify-center w-8 h-8 text-[#555] hover:text-[#858585] hover:bg-[#2a2d2e] rounded transition flex-shrink-0"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Editor area */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Tab bar */}
          {tabs.length > 0 && (
            <div className="flex items-center bg-[#252526] border-b border-[#3c3c3c] overflow-x-auto flex-shrink-0 h-9">
              {tabs.map((tab) => {
                const dirty = tab.content !== tab.originalContent
                const active = tab.filename === activeTab
                return (
                  <div
                    key={tab.filename}
                    onClick={() => setActiveTab(tab.filename)}
                    className={`flex items-center gap-1.5 px-3 h-full cursor-pointer border-r border-[#3c3c3c] select-none flex-shrink-0 group transition ${
                      active
                        ? 'tab-active bg-[#1e1e1e] text-white'
                        : 'bg-[#2d2d2d] text-[#858585] hover:bg-[#252526] hover:text-[#cccccc]'
                    }`}
                  >
                    <PythonIcon size={12} />
                    <span className="text-xs font-mono">{tab.filename}</span>
                    {dirty && <span className="text-[#0078d4] text-xs">●</span>}
                    <button
                      onClick={(e) => closeTab(tab.filename, e)}
                      className="ml-0.5 w-4 h-4 flex items-center justify-center text-[#555] hover:text-white hover:bg-[#ffffff20] rounded opacity-0 group-hover:opacity-100 transition"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Monaco editor */}
          <div className="flex-1 overflow-hidden">
            {!activeTab ? (
              /* Welcome screen */
              <div className="relative flex flex-col items-center justify-center h-full text-center select-none">
                {/* Centered branding */}
                <div className="w-16 h-16 rounded-2xl bg-[#252526] border border-[#3c3c3c] flex items-center justify-center mb-6">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3c3c3c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-[#cccccc] mb-1">SolidFlow Dev Portal</h2>
                <p className="text-sm text-[#555] max-w-xs">Select a connector file from the explorer to start editing, or upload a new one.</p>
                <div className="mt-4 flex flex-col gap-2 text-xs text-[#555]">
                  <div className="flex items-center gap-2"><kbd className="bg-[#252526] border border-[#3c3c3c] px-1.5 py-0.5 rounded text-[10px] font-mono">Ctrl+S</kbd> Save current file</div>
                </div>

                {/* Recent files — floating bottom-right */}
                {(() => {
                  const existing = new Set(connectors.map((c) => c.filename))
                  const valid = recentFiles.filter((r) => existing.has(r.filename))
                  if (valid.length === 0) return null
                  return (
                    <div className="absolute top-6 right-6 w-72 text-left">
                      <p className="text-xs text-[#555] uppercase tracking-widest mb-3 px-1">Recentes</p>
                      <div className="flex flex-col gap-2">
                        {valid.map((r) => {
                          const connector = connectors.find((c) => c.filename === r.filename)
                          const accessDate = new Date(r.accessedAt)
                          const dateStr = accessDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                          const timeStr = accessDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                          return (
                            <button
                              key={r.filename}
                              onClick={() => openFile(r.filename)}
                              className="group flex items-center gap-4 px-4 py-3 rounded-lg bg-[#1e1e1e] border border-[#2a2a2a] hover:border-[#0078d4] hover:bg-[#0078d408] transition-colors text-left"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#cccccc] truncate group-hover:text-white transition-colors">
                                  {r.displayName}
                                </p>
                                <p className="text-[11px] font-mono text-[#555] truncate mt-0.5">
                                  {r.filename}
                                </p>
                                <div className="flex items-center gap-1 mt-1.5">
                                  {connector?.approved
                                    ? <span className="text-[10px] text-[#4ec994]">● Aprovado</span>
                                    : <span className="text-[10px] text-[#e5b84a]">● Pendente</span>
                                  }
                                  <span className="text-[10px] text-[#3c3c3c]">·</span>
                                  <span className="text-[10px] text-[#444]">{dateStr} às {timeStr}</span>
                                </div>
                              </div>
                              <div className="flex-shrink-0 w-[22px] h-[22px] opacity-50 group-hover:opacity-100 transition-opacity">
                                <PythonIcon size={22} />
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}
              </div>
            ) : currentTab?.loading ? (
              <div className="flex items-center justify-center h-full gap-2 text-[#555] text-sm">
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                Loading {activeTab}…
              </div>
            ) : (
              <Editor
                height="100%"
                language="python"
                theme="vs-dark"
                value={currentTab?.content ?? ''}
                onChange={handleEditorChange}
                onMount={handleEditorMount}
                loading={
                  <div className="flex items-center justify-center h-full gap-2 text-[#555] text-sm">
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                    Loading editor…
                  </div>
                }
                options={{
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace",
                  fontLigatures: true,
                  minimap: { enabled: true, scale: 1 },
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: 'off',
                  lineNumbers: 'on',
                  renderLineHighlight: 'line',
                  tabSize: 4,
                  insertSpaces: true,
                  formatOnPaste: false,
                  suggestOnTriggerCharacters: true,
                  quickSuggestions: true,
                  padding: { top: 12, bottom: 12 },
                  smoothScrolling: true,
                  cursorBlinking: 'smooth',
                  cursorSmoothCaretAnimation: 'on',
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Delete Modal ──────────────────────────────────────────────── */}
      {deleteModal.open && deleteModal.connector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#252526] border border-[#3c3c3c] rounded-lg shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#3c3c3c] flex-shrink-0">
              <div className="flex items-center gap-2 text-[#f44747]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
                <span className="text-sm font-semibold text-white">Remover Connector</span>
              </div>
              <button
                disabled={deleteModal.deleting}
                onClick={() => setDeleteModal({ open: false, connector: null, usages: [], loadingUsages: false, confirm: '', deleting: false })}
                className="text-[#555] hover:text-white disabled:opacity-40 transition"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Connector info */}
              <div className="flex items-start gap-3 p-3 bg-[#1e1e1e] border border-[#3c3c3c] rounded">
                <PythonIcon size={18} />
                <div>
                  <div className="text-sm font-medium text-[#cccccc]">{deleteModal.connector.display_name}</div>
                  <div className="text-[11px] text-[#555] font-mono mt-0.5">{deleteModal.connector.filename}</div>
                </div>
              </div>

              {/* Usages */}
              {deleteModal.loadingUsages ? (
                <div className="flex items-center gap-2 text-xs text-[#555]">
                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Verificando uso nas integrações…
                </div>
              ) : deleteModal.usages.length > 0 ? (
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-[#f4a747] mb-2">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    Este conector está sendo usado em {deleteModal.usages.length} {deleteModal.usages.length !== 1 ? 'integrações' : 'integração'}:
                  </div>
                  <ul className="space-y-1">
                    {deleteModal.usages.map((u) => (
                      <li key={u.id} className="bg-[#1e1e1e] border border-[#3c3c3c] rounded px-2 py-1.5">
                        <div className="flex items-center gap-1.5 text-xs text-[#cccccc]">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                          {u.name}
                        </div>
                        {(u.supplier || u.system) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {u.supplier && (
                              <span className="text-[10px] text-[#6cb6f5] bg-[#0078d415] border border-[#0078d430] rounded px-1.5 py-0.5">
                                {u.supplier}
                              </span>
                            )}
                            {u.system && (
                              <span className="text-[10px] text-[#c5a0f5] bg-[#7c3aed15] border border-[#7c3aed30] rounded px-1.5 py-0.5">
                                {u.system}
                              </span>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-[#858585] mt-2">Ao remover, essas integrações perderão a referência ao conector.</p>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs text-[#4ec994]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Nenhuma integração utiliza este conector.
                </div>
              )}

              {/* Confirmation input */}
              <div>
                <label className="block text-xs text-[#858585] mb-1.5">
                  Digite <code className="bg-[#1e1e1e] border border-[#3c3c3c] px-1 rounded text-[#f44747]">REMOVER</code> para confirmar
                </label>
                <input
                  autoFocus
                  type="text"
                  value={deleteModal.confirm}
                  onChange={(e) => setDeleteModal((m) => ({ ...m, confirm: e.target.value }))}
                  placeholder="REMOVER"
                  className="w-full bg-[#1e1e1e] border border-[#3c3c3c] focus:border-[#f44747] rounded px-3 py-2 text-sm text-[#cccccc] placeholder-[#555] outline-none transition"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#3c3c3c] flex-shrink-0">
              <button
                disabled={deleteModal.deleting}
                onClick={() => setDeleteModal({ open: false, connector: null, usages: [], loadingUsages: false, confirm: '', deleting: false })}
                className="px-4 py-1.5 text-sm text-[#858585] hover:text-white hover:bg-[#3c3c3c] disabled:opacity-40 rounded transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteModal.confirm !== 'REMOVER' || deleteModal.deleting || deleteModal.loadingUsages}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#f44747] hover:bg-[#ff5555] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded transition"
              >
                {deleteModal.deleting ? (
                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  </svg>
                )}
                {deleteModal.deleting ? 'Removendo…' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload Modal ──────────────────────────────────────────────── */}
      {showUploadModal && pendingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#252526] border border-[#3c3c3c] rounded-lg shadow-2xl w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#3c3c3c]">
              <div className="flex items-center gap-2">
                <PythonIcon size={16} />
                <span className="text-sm font-semibold text-white">Upload Connector</span>
              </div>
              <button onClick={() => { setShowUploadModal(false); setPendingFile(null) }} className="text-[#555] hover:text-white transition">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              {/* File info */}
              <div className="flex items-center gap-2 px-3 py-2 bg-[#1e1e1e] border border-[#3c3c3c] rounded text-xs font-mono text-[#858585]">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                {pendingFile.name}
              </div>

              {/* Display name */}
              <div>
                <label className="block text-xs text-[#858585] mb-1.5">
                  Nome de exibição <span className="text-[#f44747]">*</span>
                </label>
                <input
                  autoFocus
                  type="text"
                  value={uploadForm.displayName}
                  onChange={(e) => setUploadForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="Ex: LogicMed Worklist"
                  className="w-full bg-[#1e1e1e] border border-[#3c3c3c] focus:border-[#0078d4] rounded px-3 py-2 text-sm text-[#cccccc] placeholder-[#555] outline-none transition"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs text-[#858585] mb-1.5">Descrição <span className="text-[#555] font-normal">(opcional)</span></label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Breve descrição do que este conector faz…"
                  rows={3}
                  className="w-full bg-[#1e1e1e] border border-[#3c3c3c] focus:border-[#0078d4] rounded px-3 py-2 text-sm text-[#cccccc] placeholder-[#555] outline-none transition resize-none"
                />
              </div>

              {/* Approved */}
              <label className={`flex items-center gap-3 select-none ${canApprove(username) ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                <div
                  onClick={() => {
                    if (!canApprove(username)) {
                      showToast('error', `Usuário ${username} sem permissão para aprovar conectores.`)
                      return
                    }
                    setUploadForm((f) => ({ ...f, approved: !f.approved }))
                  }}
                  className={`relative w-9 h-5 rounded-full transition-colors ${uploadForm.approved ? 'bg-[#4ec994]' : 'bg-[#3c3c3c]'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${uploadForm.approved ? 'translate-x-4' : ''}`} />
                </div>
                <div>
                  <span className="text-sm text-[#cccccc]">Já aprovado</span>
                  <p className="text-[11px] text-[#555] mt-0.5">Disponibiliza imediatamente para uso nas integrações</p>
                </div>
              </label>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#3c3c3c]">
              <button
                onClick={() => { setShowUploadModal(false); setPendingFile(null) }}
                className="px-4 py-1.5 text-sm text-[#858585] hover:text-white hover:bg-[#3c3c3c] rounded transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={!uploadForm.displayName.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0078d4] hover:bg-[#1a8ad4] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded transition"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Run Modal ─────────────────────────────────────────────────── */}
      {showRunModal && activeTab && (
        <RunModal
          filename={activeTab}
          code={currentTab?.content ?? ''}
          onClose={() => setShowRunModal(false)}
        />
      )}

      {/* ── New Connector Modal ───────────────────────────────────────── */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#252526] border border-[#3c3c3c] rounded-lg shadow-2xl w-full max-w-lg mx-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#3c3c3c] flex-shrink-0">
              <div className="flex items-center gap-2">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6cb6f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span className="text-sm font-semibold text-white">New Connector</span>
              </div>
              <button onClick={() => setShowNewModal(false)} className="text-[#555] hover:text-white transition">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-5">
              {/* Filename */}
              <div>
                <label className="block text-xs text-[#858585] mb-1.5">
                  Filename <span className="text-[#f44747]">*</span>
                </label>
                <input
                  autoFocus
                  type="text"
                  value={newFilename}
                  onChange={(e) => { setNewFilename(e.target.value); setNewFileError('') }}
                  placeholder="my_connector_connector.py"
                  className={`w-full bg-[#1e1e1e] border rounded px-3 py-2 text-sm text-[#cccccc] font-mono placeholder-[#555] outline-none transition ${
                    newFileError ? 'border-[#f44747]' : 'border-[#3c3c3c] focus:border-[#0078d4]'
                  }`}
                />
                <p className="mt-1 text-[11px] text-[#555]">Must end with <code className="text-[#858585]">_connector.py</code> · lowercase letters, numbers and _ only</p>
              </div>

              {/* Type selector */}
              <div>
                <label className="block text-xs text-[#858585] mb-2.5">Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Blank */}
                  <button
                    onClick={() => { setNewType('blank'); setNewFileError('') }}
                    className={`flex flex-col items-center gap-2 px-3 py-4 rounded-lg border transition ${
                      newType === 'blank'
                        ? 'border-[#0078d4] bg-[#0078d415] text-white'
                        : 'border-[#3c3c3c] hover:border-[#505050] hover:bg-[#2a2d2e] text-[#858585] hover:text-[#cccccc]'
                    }`}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    <span className="text-xs font-medium">Blank</span>
                    <span className="text-[10px] text-[#555] text-center leading-tight">Empty file</span>
                  </button>

                  {/* Template */}
                  <button
                    onClick={() => { setNewType('template'); setNewFileError('') }}
                    className={`flex flex-col items-center gap-2 px-3 py-4 rounded-lg border transition ${
                      newType === 'template'
                        ? 'border-[#4ec994] bg-[#4ec99415] text-white'
                        : 'border-[#3c3c3c] hover:border-[#505050] hover:bg-[#2a2d2e] text-[#858585] hover:text-[#cccccc]'
                    }`}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="9" y1="21" x2="9" y2="9"/>
                    </svg>
                    <span className="text-xs font-medium">Template</span>
                    <span className="text-[10px] text-[#555] text-center leading-tight">Full boilerplate</span>
                  </button>

                  {/* From */}
                  <button
                    onClick={() => { setNewType('from'); setNewFileError('') }}
                    className={`flex flex-col items-center gap-2 px-3 py-4 rounded-lg border transition ${
                      newType === 'from'
                        ? 'border-[#ddb900] bg-[#ddb90015] text-white'
                        : 'border-[#3c3c3c] hover:border-[#505050] hover:bg-[#2a2d2e] text-[#858585] hover:text-[#cccccc]'
                    }`}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                    </svg>
                    <span className="text-xs font-medium">From</span>
                    <span className="text-[10px] text-[#555] text-center leading-tight">Copy existing</span>
                  </button>
                </div>
              </div>

              {/* From — connector selector */}
              {newType === 'from' && (
                <div>
                  <label className="block text-xs text-[#858585] mb-1.5">
                    Source connector <span className="text-[#f44747]">*</span>
                  </label>
                  {connectors.length === 0 ? (
                    <p className="text-xs text-[#555] italic">No connectors available to copy from.</p>
                  ) : (
                    <>
                    <div className="flex items-center gap-1.5 bg-[#1e1e1e] border border-[#3c3c3c] rounded px-2 py-1 mb-1.5">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        type="text"
                        value={newFromFilter}
                        onChange={(e) => setNewFromFilter(e.target.value)}
                        placeholder="Filter connectors…"
                        className="flex-1 bg-transparent text-xs text-[#cccccc] placeholder-[#555] outline-none"
                      />
                      {newFromFilter && (
                        <button onClick={() => setNewFromFilter('')} className="text-[#555] hover:text-[#858585]">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="max-h-36 overflow-y-auto rounded border border-[#3c3c3c] divide-y divide-[#2a2a2a]">
                      {connectors.filter((c) =>
                        c.display_name.toLowerCase().includes(newFromFilter.toLowerCase()) ||
                        c.filename.toLowerCase().includes(newFromFilter.toLowerCase())
                      ).map((c) => (
                        <button
                          key={c.filename}
                          onClick={() => { setNewFromFile(c.filename); setNewFileError('') }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition ${
                            newFromFile === c.filename
                              ? 'bg-[#0078d420] text-white'
                              : 'text-[#858585] hover:bg-[#2a2d2e] hover:text-[#cccccc]'
                          }`}
                        >
                          <PythonIcon size={12} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{c.display_name}</p>
                            <p className="text-[10px] font-mono text-[#555] truncate">{c.filename}</p>
                          </div>
                          {newFromFile === c.filename && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6cb6f5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </button>
                      ))}
                    </div>
                    </>
                  )}
                </div>
              )}

              {/* Error */}
              {newFileError && (
                <p className="text-xs text-[#f44747] flex items-center gap-1.5">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {newFileError}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#3c3c3c] flex-shrink-0">
              <button
                onClick={() => setShowNewModal(false)}
                className="px-4 py-1.5 text-sm text-[#858585] hover:text-white hover:bg-[#3c3c3c] rounded transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateConnector}
                disabled={newCreating || !newType}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#0078d4] hover:bg-[#1a8ad4] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded transition"
              >
                {newCreating ? (
                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                )}
                {newCreating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocs && <DocsModal onClose={() => setShowDocs(false)} />}

      {/* ── Snippets Modal ────────────────────────────────────────────── */}
      <SnippetsModal
        isOpen={showSnippets}
        onClose={() => setShowSnippets(false)}
        onSelect={insertSnippet}
      />

      {/* ── Save Status Choice Modal ──────────────────────────────────── */}
      {saveStatusModal.open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg shadow-2xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#3c3c3c]">
              <h2 className="text-base font-semibold text-white">Salvar conector</h2>
              <button
                onClick={() => setSaveStatusModal({ open: false, filename: null, selectedStatus: 'pending' })}
                className="text-[#858585] hover:text-white transition"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-[#ababab] leading-relaxed">
                Como você deseja salvar <code className="bg-[#2d2d2d] px-2 py-1 rounded text-[#ce9178]">{saveStatusModal.filename}</code>?
              </p>

              <div className="space-y-3">
                {/* Approved option */}
                <label className="flex items-start gap-3 p-3 bg-[#252526] border border-[#3c3c3c] rounded cursor-pointer hover:border-[#4ec994] transition">
                  <input
                    type="radio"
                    name="status"
                    value="approved"
                    checked={saveStatusModal.selectedStatus === 'approved'}
                    onChange={(e) => setSaveStatusModal((m) => ({ ...m, selectedStatus: e.target.value }))}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#4ec994]">✓ Salvar como aprovado</p>
                    <p className="text-xs text-[#858585] mt-1">O conector será marcado como aprovado imediatamente</p>
                  </div>
                </label>

                {/* Pending option */}
                <label className="flex items-start gap-3 p-3 bg-[#252526] border border-[#3c3c3c] rounded cursor-pointer hover:border-[#ddb900] transition">
                  <input
                    type="radio"
                    name="status"
                    value="pending"
                    checked={saveStatusModal.selectedStatus === 'pending'}
                    onChange={(e) => setSaveStatusModal((m) => ({ ...m, selectedStatus: e.target.value }))}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#ddb900]">⟳ Manter em revisão</p>
                    <p className="text-xs text-[#858585] mt-1">O conector permanecerá em status pendente e precisará ser aprovado manualmente</p>
                  </div>
                </label>
              </div>

              <p className="text-xs text-[#555] pt-2">
                Sua escolha será salva como padrão para futuras operações
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#3c3c3c]">
              <button
                onClick={() => setSaveStatusModal({ open: false, filename: null, selectedStatus: 'pending' })}
                className="px-4 py-2 text-sm font-medium text-[#858585] hover:text-white bg-[#2d2d2d] hover:bg-[#3c3c3c] border border-[#3c3c3c] rounded transition"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleConfirmSave(saveStatusModal.selectedStatus)}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0078d4] hover:bg-[#005a9e] disabled:bg-[#0078d480] border border-[#0078d4] rounded transition disabled:cursor-not-allowed"
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Status Bar ────────────────────────────────────────────────── */}
      <footer className="flex items-center justify-between px-3 h-6 bg-[#007acc] text-white text-[11px] flex-shrink-0 select-none">
        <div className="flex items-center gap-3">
          {activeTab ? (
            <>
              <span className="flex items-center gap-1">
                <PythonIcon size={11} />
                Python
              </span>
              <span>Ln {cursor.line}, Col {cursor.col}</span>
              {isDirty && (
                <span className="flex items-center gap-1 opacity-80">
                  <span className="w-1.5 h-1.5 rounded-full bg-white inline-block" />
                  Unsaved changes
                </span>
              )}
            </>
          ) : (
            <span className="opacity-75">No file open</span>
          )}
        </div>
        <div className="flex items-center gap-3 opacity-80">
          <span>UTF-8</span>
          <span>LF</span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4ec994]" />
            Dev Portal
          </span>
        </div>
      </footer>
    </div>
  )
}
